import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Seo from '../components/Seo'
import ContactForm from '../components/ContactForm'
import FullBleedGalleryPanels from '../components/FullBleedGalleryPanels'
import { getOptimizedImageUrl, getImageSrcSet } from '../lib/image'
import { getCmsPage, getCompanyInfo, getCmsCollection } from '../lib/cms'

const DEFAULT_HERO_IMAGE = 'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/27783477-pexels-photo-27783477-auto-compress-cs-tinysrgb-dpr-2-h-650-w-940.jpeg'
const DEFAULT_ABOUT_IMAGE = 'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-1267.jpeg'

export default function HomePage() {
  const [pageData, setPageData] = useState(null)
  const [companyInfo, setCompanyInfo] = useState(null)
  const [featuredServices, setFeaturedServices] = useState([])

  useEffect(() => {
    async function loadData() {
      try {
        const [page, info, services] = await Promise.all([
          getCmsPage('forside'),
          getCompanyInfo(),
          getCmsCollection('tjenester'),
        ])
        if (page) setPageData(page)
        if (info) setCompanyInfo(info)
        if (Array.isArray(services) && services.length > 0) {
          const featured = services.filter((s) => s.is_featured)
          if (featured.length >= 3) {
            setFeaturedServices(featured.slice(0, 3))
          } else {
            const sorted = [...services].sort((a, b) => (a.order || 0) - (b.order || 0))
            setFeaturedServices(sorted.slice(0, 3))
          }
        }
      } catch (err) {
        console.error('Error loading HomePage CMS data:', err)
      }
    }
    loadData()
  }, [])

  const heroTitle = pageData?.hero_title || 'Effektive maskiner til dit byggeprojekt.'
  const heroDescription = pageData?.hero_description || 'Velkommen til Lyn Entreprenør Aps - din pålidelige partner inden for jordarbejde på Fyn. Vi leverer effektive og kvalitetsbevidste løsninger til byggeprojekter, der sikrer vores kunders succes.'
  const heroImage = pageData?.hero_image?.[0] || DEFAULT_HERO_IMAGE

  const section1Badge = pageData?.section_badge || 'Virksomheden bag'
  const section1Title = pageData?.section_title || 'Erfarne entreprenører på Fyn'
  const section1Text = pageData?.section_text
    ? (typeof pageData.section_text === 'string' && pageData.section_text.startsWith('<') ? pageData.section_text.replace(/<[^>]+>/g, '') : pageData.section_text)
    : 'Lyn Entreprenør Aps er en professionel virksomhed på Fyn, der specialiserer sig i jordarbejde og levering af maskiner og udstyr til byggeri. Vi tilbyder effektive og pålidelige løsninger til vores kunder.'
  const section1Image = pageData?.section_image?.[0] || DEFAULT_ABOUT_IMAGE

  const phone = companyInfo?.phone || '+45 55236932'
  const phoneClean = phone.replace(/[^0-9+]/g, '')
  const email = companyInfo?.email_primary || 'Info@Lyn-Ent.dk'
  const address = companyInfo?.address || 'krogsberg 1'
  const city = companyInfo?.postal_and_city || '5683 Haarby, Danmark'
  const cvr = companyInfo?.cvr || '46264126'
  const facebook = companyInfo?.facebook_url || 'https://www.facebook.com/LynEntrepreneor'
  return (
    <>
      <Seo
        title="Lyn Entreprenør"
        description="Effektive maskiner til dit byggeprojekt. Velkommen til Lyn Entreprenør Aps - din pålidelige partner inden for jordarbejde på Fyn."
        ogImage="https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/fulllogo-nobuffer-5_1e74d9a2.png"
        canonicalUrl="https://www.lyn-ent.dk/"
      />

      {/* Hero Section (Light, Premium, Powerful Contractor aesthetic) */}
      <section className="relative overflow-hidden bg-slate-900 min-h-[550px] sm:min-h-[650px] lg:min-h-[720px] flex items-center">
        {/* Full-bleed background image */}
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl(
              heroImage,
              { width: 1920, height: 1080, quality: 85 }
            )}
            srcSet={getImageSrcSet(
              heroImage,
              [800, 1200, 1600, 1920]
            )}
            sizes="100vw"
            alt="Lyn Entreprenør gravning og maskiner i aktion"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/75 to-slate-950/40" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-16 sm:py-24 w-full">
          <div className="max-w-2xl space-y-6 text-left">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1]">
              {heroTitle}
            </h1>

            <p className="text-lg sm:text-xl text-slate-200 leading-relaxed font-normal">
              {heroDescription}
            </p>

            {/* Action buttons */}
            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center rounded-xl bg-red-600 px-7 py-4 text-base font-bold text-white transition duration-200 hover:bg-red-700 active:scale-[0.98]"
              >
                <span>Kontakt os</span>
              </Link>

              <Link
                to="/tjenester"
                className="inline-flex items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm px-6 py-4 text-base font-bold text-white border border-white/20 transition duration-200 hover:bg-white/20 hover:border-white/40"
              >
                <span>Se alle ydelser</span>
              </Link>
            </div>
          </div>
        </div>

        {/* V-shaped divider at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg
            className="relative block w-full h-8 sm:h-12 md:h-16 lg:h-20 text-white"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M0,0 L600,120 L1200,0 L1200,120 L0,120 Z" />
          </svg>
        </div>
      </section>

      {/* Section 2: Erfarne entreprenører på Fyn */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-28 xl:gap-32">
            {/* Left Image collage with real work photo (desktop width) */}
            <div className="w-full lg:w-[56%] order-2 lg:order-1">
              <div className="relative max-w-full">
                <div className="relative rounded-t-2xl overflow-hidden aspect-[16/11] min-h-[260px] sm:min-h-[380px] lg:min-h-[520px] max-w-full">
                  <img
                    src={getOptimizedImageUrl(
                      section1Image,
                      { width: 1200, height: 850, quality: 85 }
                    )}
                    alt="Lyn Entreprenør jordarbejde og udstyr"
                    className="w-full h-full max-w-full object-cover"
                  />
                  {/* V-shaped divider at the bottom of the image */}
                  <div className="absolute -bottom-0.5 -left-px -right-px w-[calc(100%+2px)] overflow-hidden leading-none z-10 pointer-events-none">
                    <svg
                      className="relative block w-full h-6 sm:h-8 md:h-10 text-white"
                      viewBox="0 0 1200 120"
                      preserveAspectRatio="none"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M0,0 L600,120 L1200,0 L1200,120 L0,120 Z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Right text content (desktop width) */}
            <div className="w-full lg:flex-1 order-1 lg:order-2 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-red-600">
                <span>{section1Badge}</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                {section1Title}
              </h2>

              <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                {section1Text}
              </p>

              <div className="pt-2">
                <Link
                  to="/om-os"
                  className="inline-flex items-center gap-2 text-base font-bold text-red-600 hover:text-red-700 transition group"
                >
                  <span>Læs mere om os</span>
                  <i className="fa-solid fa-arrow-right text-sm transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Jordarbejde på Fyn (Core Services Overview) */}
      <section className="py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14 text-left">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-red-600 mb-2">
              <span>Kompetencer & Ydelser</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Jordarbejde på Fyn
            </h2>
            <p className="mt-3 text-base sm:text-lg text-slate-600 leading-relaxed">
              Lyn Entreprenør Aps er din pålidelige partner inden for jordarbejde på Fyn. Vi tilbyder effektive og professionelle løsninger til alle former for byggeri, og vores moderne maskiner og udstyr sikrer en hurtig og præcis udførelse af opgaverne. Kontakt os i dag for et uforpligtende tilbud.
            </p>
          </div>

          {/* 3 Prominent Service Cards from CMS */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {(featuredServices.length > 0
              ? featuredServices
              : [
                  {
                    title: 'Gravning og udgravning',
                    short_description: '#værdi er en hashtag, der ofte bruges på sociale medier som Twitter, Instagram og Facebook...',
                    featured_image: ['https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-1267.jpeg'],
                    slug: 'gravning-og-udgravning',
                  },
                  {
                    title: 'Funderingsarbejde',
                    short_description: '#værdi er en hashtag, der ofte bruges i sociale medier og online platforme til at angive værdien...',
                    featured_image: ['https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-0845.jpeg'],
                    slug: 'funderingsarbejde',
                  },
                  {
                    title: 'Jordafgravning og nivellering',
                    short_description: '#værdi er en hashtag, der ofte bruges i sociale medier til at udtrykke værdi og betydning...',
                    featured_image: ['https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-3030.jpeg'],
                    slug: 'jordafgravning-og-nivellering',
                  },
                ]
            ).map((service, idx) => {
              const sSlug = typeof service.slug === 'object' ? service.slug?.da : service.slug
              const cleanSlug = sSlug ? sSlug.replace(/^tjeneste-|^service-/, '') : ''
              const imgUrl = service.featured_image?.[0] || DEFAULT_ABOUT_IMAGE
              return (
                <Link
                  key={service.id || idx}
                  to={`/tjenester/${cleanSlug}`}
                  className="bg-white rounded-2xl border border-slate-200/80 shadow-sm transition duration-300 hover:shadow-md hover:border-slate-300 flex flex-col overflow-hidden group"
                >
                  <div className="relative w-full aspect-[16/10] overflow-hidden bg-slate-100">
                    <img
                      src={getOptimizedImageUrl(imgUrl, { width: 600, height: 400, quality: 80 })}
                      alt={service.title}
                      className="w-full h-full object-cover transition duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute -bottom-0.5 -left-px -right-px w-[calc(100%+2px)] overflow-hidden leading-none z-10 pointer-events-none">
                      <svg
                        className="relative block w-full h-5 sm:h-6 md:h-7 text-white"
                        viewBox="0 0 1200 120"
                        preserveAspectRatio="none"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M0,0 L600,120 L1200,0 L1200,120 L0,120 Z" />
                      </svg>
                    </div>
                  </div>

                  <div className="p-7 sm:p-8 flex flex-col flex-1">
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3 group-hover:text-red-600 transition-colors tracking-tight">
                      {service.title}
                    </h3>
                    <p className="text-sm sm:text-[15px] text-slate-600 leading-relaxed line-clamp-2">
                      {service.short_description || service.title}
                    </p>
                  </div>
                </Link>
              )
            })}
          </div>

          <div className="text-center">
            <Link
              to="/tjenester"
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-7 py-3.5 text-sm font-bold text-white transition duration-200 hover:bg-slate-800 active:scale-95"
            >
              <span>Alle ydelser</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Full-bleed 4-column Gallery Panels */}
      <FullBleedGalleryPanels slug="hovedgalleri" />

      {/* Section 5: Kontakt Teaser with Form */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Contact Info */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                  Kontakt
                </h2>
                <p className="mt-3 text-base text-slate-600 leading-relaxed">
                  Velkommen til Lyn Entreprenør Aps - din pålidelige partner inden for maskiner og udstyr til byggeri på Fyn.
                </p>
              </div>

              {/* Direct Info Cards */}
              <div className="space-y-4 pt-2">
                <div>
                  <h4 className="text-sm font-bold text-slate-900">{companyInfo?.company_name || 'Lyn Entreprenør Aps'}</h4>
                  <p className="text-xs text-slate-600">{address}, {city}</p>
                  <p className="text-xs text-slate-500 mt-0.5">CVR: {cvr}</p>
                </div>

                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Telefon</h4>
                  <a
                    href={`tel:${phoneClean}`}
                    className="text-base font-bold text-slate-900 hover:text-red-600 transition whitespace-nowrap"
                  >
                    {phone}
                  </a>
                </div>

                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</h4>
                  <a
                    href={`mailto:${email}`}
                    className="text-base font-bold text-slate-900 hover:text-red-600 transition whitespace-nowrap"
                  >
                    {email}
                  </a>
                </div>

                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Facebook</h4>
                  <a
                    href={facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold text-slate-900 hover:text-red-600 transition"
                  >
                    {facebook.replace('https://', '')}
                  </a>
                </div>
              </div>
            </div>

            {/* Right Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
